import React, { useEffect } from "react";
import { graphql, useStaticQuery } from "gatsby";
import { Helmet } from "react-helmet";
import { useLocation } from "@reach/router";

import { Footer } from "./Footer";
import { Header } from "./Header";
import { site, apps, absoluteUrl } from "../config/site";

export const Layout = ({
  metaTitle,
  metaDescription,
  ogType = "website",
  structuredData = [],
  children,
}) => {
  const location = useLocation();

  useEffect(() => {
    if (
      typeof window !== "undefined" &&
      (window.location.hostname === "schengen-simple.web.app" ||
        window.location.hostname === "schengen-simple.firebaseapp.com")
    ) {
      window.location.href = "https://schengensimple.com";
    } else return undefined;
  }, []);

  const headData = useStaticQuery(graphql`
    {
      site {
        siteMetadata {
          title
          description
        }
      }
    }
  `);

  // The fallback title already carries the brand, so only page titles get the
  // suffix.
  const title = metaTitle
    ? `${metaTitle} | ${site.name}`
    : headData.site.siteMetadata.title;
  const socialTitle = metaTitle || headData.site.siteMetadata.title;
  const description =
    metaDescription || headData.site.siteMetadata.description;
  const pageUrl = absoluteUrl(location.pathname);

  return (
    <>
      <Helmet>
        <meta charSet="utf-8" />
        <title>{title}</title>
        <meta name="description" content={description} />
        <meta property="image" content={site.socialImageSquare} />

        {/* Open Graph tags */}
        <meta property="og:title" content={socialTitle} />
        <meta property="og:type" content={ogType} />
        <meta property="og:url" content={pageUrl} />
        <meta property="og:site_name" content={site.name} />
        <meta property="og:locale" content={site.locale} />
        <meta property="og:description" content={description} />
        <meta property="og:image" content={site.socialImageLandscape} />

        {/* Twitter tags */}
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content={socialTitle} />
        <meta name="twitter:description" content={description} />
        <meta name="twitter:image" content={site.socialImageLandscape} />

        {site.verification.google && (
          <meta
            name="google-site-verification"
            content={site.verification.google}
          />
        )}
        {site.verification.bing && (
          <meta name="msvalidate.01" content={site.verification.bing} />
        )}

        {/* iOS Smart App Banner */}
        <meta
          name="apple-itunes-app"
          content={`app-id=${apps.ios.appId}`}
        />

        {/* Other tags */}
        <meta name="viewport" content="width=device-width, initial-scale=1.0" />
        <link rel="canonical" href={pageUrl} />
        <link rel="preconnect" href="https://fonts.googleapis.com" />
        <link rel="preconnect" href="https://fonts.gstatic.com" crossorigin />
        <link
          href="https://fonts.googleapis.com/css2?family=Inter:wght@100;300;500;700&display=swap"
          rel="stylesheet"
        />

        {structuredData.filter(Boolean).map((item, i) => (
          <script type="application/ld+json" key={`ld-${i}`}>
            {JSON.stringify(item)}
          </script>
        ))}
      </Helmet>

      <Header />
      <main>{children}</main>
      <Footer />
    </>
  );
};
